import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, PawPrint, Edit2, Trash2, Camera, Building2, FileText, Loader2, ZoomIn, Check, ChevronRight } from 'lucide-react';
import toast from 'react-hot-toast';
import { Pet, condoApi } from '../services/api';
import PhotoUploadModal, { PhotoResult } from './PhotoUploadModal';
import PetFormModal from './PetFormModal';
import { CondoType, getUnitLabel, formatUnitLabel } from '../utils/terminology';
import { usePersonDetailContext } from '../contexts/PersonDetailContext';
import { PET_SPECIES_LABELS } from '../utils/petLabels';

// Legacy pets were seeded with raw English enum values (species/sex/size); newer ones already
// store the Portuguese labels used by PetFormModal's selects. Map the English values for display
// so both old and new records read consistently — anything not in the map passes through as-is.
const SEX_LABELS: Record<string, string> = { male: 'Macho', female: 'Fêmea' };
const SIZE_LABELS: Record<string, string> = { mini: 'Miniatura', small: 'Pequeno', medium: 'Médio', large: 'Grande', giant: 'Gigante' };

const translatePetField = (value: string | null | undefined, map: Record<string, string>): string | undefined =>
  value ? (map[value.toLowerCase()] ?? value) : undefined;

interface PetDetailModalProps {
  pet: Pet;
  customerId: string;
  condoType?: CondoType;
  onClose: () => void;
  onPetUpdated: () => void;
  onPetDeleted: () => void;
  lightTheme?: boolean;
}

const PetDetailModal: React.FC<PetDetailModalProps> = ({
  pet: initialPet,
  customerId,
  condoType,
  onClose,
  onPetUpdated,
  onPetDeleted,
  lightTheme = false,
}) => {
  const navigate = useNavigate();
  const { onUnitClick } = usePersonDetailContext();
  const [pet, setPet] = useState<Pet>(initialPet);
  const [showEditForm, setShowEditForm] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [activeSlot, setActiveSlot] = useState<number>(1);
  const [uploadingSlot, setUploadingSlot] = useState<number | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [lightboxPhoto, setLightboxPhoto] = useState<string | null>(null);

  const [photoPreviews, setPhotoPreviews] = useState<(string | null)[]>([
    initialPet.photo_url || null,
    initialPet.photo_url_2 || null,
    initialPet.photo_url_3 || null,
    initialPet.photo_url_4 || null,
  ]);

  const handlePhotoClick = (slot: number) => {
    if (isEditMode) {
      setActiveSlot(slot);
      setShowPhotoModal(true);
    } else {
      const photo = photoPreviews[slot - 1];
      if (photo) setLightboxPhoto(photo);
    }
  };

  const handlePhotoCropped = async (result: PhotoResult) => {
    setShowPhotoModal(false);
    setUploadingSlot(activeSlot);
    try {
      const file = new File([result.blob], 'photo.jpg', { type: result.blob.type || 'image/jpeg' });
      await condoApi.uploadPetPhoto(customerId, pet.id, file, activeSlot);
      const newPreviews = [...photoPreviews];
      newPreviews[activeSlot - 1] = result.base64;
      setPhotoPreviews(newPreviews);
      toast.success('Foto atualizada!');
      onPetUpdated();
    } catch {
      toast.error('Erro ao enviar foto');
    } finally {
      setUploadingSlot(null);
    }
  };

  const handleEditSuccess = async () => {
    setShowEditForm(false);
    try {
      const res = await condoApi.listPets(customerId);
      const updated = res?.pets?.find((p: Pet) => p.id === pet.id);
      if (updated) {
        setPet(updated);
        setPhotoPreviews([
          updated.photo_url || null,
          updated.photo_url_2 || null,
          updated.photo_url_3 || null,
          updated.photo_url_4 || null,
        ]);
      }
    } catch {}
    onPetUpdated();
  };

  // When rendered from inside OperationalView, it already registered a live onUnitClick
  // handler (via PersonDetailContext) that jumps straight to the location-detail view — use
  // it directly so the click responds immediately. Otherwise (the standalone Pets page,
  // outside OperationalView) fall back to stashing the unit and navigating to /dashboard,
  // which OperationalView picks up from localStorage on mount.
  const handleUnitClick = () => {
    if (!pet.unit_id || !pet.unit) return;
    if (onUnitClick) {
      onClose();
      onUnitClick(pet.unit_id, pet.unit.number, pet.unit.block?.id);
      return;
    }
    localStorage.setItem('panther_operational_pending_unit', JSON.stringify({
      id: pet.unit_id,
      name: pet.unit.number,
      blockId: pet.unit.block?.id,
    }));
    onClose();
    navigate('/dashboard');
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await condoApi.deletePet(customerId, pet.id);
      toast.success('Pet removido');
      onPetDeleted();
    } catch {
      toast.error('Erro ao remover pet');
      setDeleting(false);
    }
  };

  const surface = lightTheme ? 'bg-white border-gray-200' : 'bg-gray-900 border-gray-700';
  const divider = lightTheme ? 'border-gray-100' : 'border-gray-800';
  const labelCls = lightTheme ? 'text-gray-400' : 'text-gray-500';
  const textCls = lightTheme ? 'text-gray-800' : 'text-gray-200';
  const subtextCls = lightTheme ? 'text-gray-500' : 'text-gray-400';
  const descBg = lightTheme ? 'bg-gray-50 text-gray-700' : 'bg-gray-800/60 text-gray-300';
  const photoSlotCls = lightTheme
    ? 'bg-gray-100 border-gray-200 hover:border-amber-400'
    : 'bg-gray-800 border-gray-700 hover:border-amber-600';

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-black/70" onClick={onClose} />

        <div className={`relative z-10 w-full max-w-lg rounded-2xl shadow-2xl border flex flex-col max-h-[92vh] overflow-hidden ${surface}`}>

          {/* Header */}
          <div className={`flex items-center justify-between px-5 py-3.5 border-b flex-shrink-0 ${divider}`}>
            <span className={`text-[11px] font-semibold uppercase tracking-widest ${labelCls}`}>Detalhes do Pet</span>
            <button onClick={onClose} className={`rounded-lg p-1 transition-colors ${lightTheme ? 'text-gray-400 hover:text-gray-600 hover:bg-gray-100' : 'text-gray-500 hover:text-gray-200 hover:bg-gray-800'}`}>
              <X className="h-4.5 w-4.5" />
            </button>
          </div>

          {/* Scrollable body */}
          <div className="overflow-y-auto flex-1">

            {/* Top section — photos in a big left column, name/breed + info grid together in
                a right column. Photos are a fixed 4:5 box (matching the cropper's
                cropShape="rect" aspectRatio={4/5} output, same as the person identity photo),
                scaled up here since this is the primary detail view, not a compact list row. */}
            <div className={`flex items-center gap-5 p-5 flex-shrink-0 border-b ${divider}`}>
              {/* Photo column */}
              <div className="flex gap-2 flex-shrink-0">
                {/* Main photo */}
                <button
                  type="button"
                  onClick={() => handlePhotoClick(1)}
                  disabled={uploadingSlot !== null}
                  className="relative group w-40 h-[200px] flex-shrink-0 rounded-lg overflow-hidden"
                >
                  {photoPreviews[0] ? (
                    <img src={photoPreviews[0]} alt={pet.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className={`w-full h-full flex items-center justify-center ${lightTheme ? 'bg-amber-50' : 'bg-amber-900/10'}`}>
                      <PawPrint className="h-16 w-16 text-amber-300/30" />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <div className="bg-white rounded-full p-2.5 shadow-xl">
                      {uploadingSlot === 1
                        ? <Loader2 className="h-5 w-5 text-gray-700 animate-spin" />
                        : isEditMode
                          ? <Camera className="h-5 w-5 text-gray-700" />
                          : photoPreviews[0] ? <ZoomIn className="h-5 w-5 text-gray-700" /> : <Camera className="h-5 w-5 text-gray-700" />
                      }
                    </div>
                  </div>
                </button>

                {/* Slots 2, 3 & 4 stacked — same 4:5 ratio, scaled down */}
                <div className="flex flex-col gap-2">
                  {([2, 3, 4] as const).map((slot) => {
                    const hasPhoto = !!photoPreviews[slot - 1];
                    return (
                      <button
                        key={slot}
                        type="button"
                        onClick={() => handlePhotoClick(slot)}
                        disabled={uploadingSlot !== null}
                        className="relative group w-16 h-20 flex-shrink-0 rounded-lg overflow-hidden"
                      >
                        {hasPhoto ? (
                          <img src={photoPreviews[slot - 1]!} alt={`Foto ${slot}`} className="w-full h-full object-cover" />
                        ) : (
                          <div className={`w-full h-full flex flex-col items-center justify-center gap-1 transition-colors ${lightTheme ? 'bg-gray-100 text-gray-400 group-hover:text-amber-500' : 'bg-gray-800/60 text-gray-600 group-hover:text-amber-500'}`}>
                            <Camera className="h-4 w-4" />
                            <span className="text-[8px] font-medium uppercase tracking-wider">Foto {slot}</span>
                          </div>
                        )}
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                          <div className="bg-white rounded-full p-1.5 shadow-xl">
                            {uploadingSlot === slot
                              ? <Loader2 className="h-3.5 w-3.5 text-gray-700 animate-spin" />
                              : isEditMode
                                ? <Camera className="h-3.5 w-3.5 text-gray-700" />
                                : hasPhoto ? <ZoomIn className="h-3.5 w-3.5 text-gray-700" /> : <Camera className="h-3.5 w-3.5 text-gray-700" />
                            }
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Details column — name/breed + info grid together, so the space beside the
                  photos is actually used instead of left as dead whitespace. */}
              <div className="flex-1 min-w-0 space-y-4">
                <div>
                  <h3 className={`text-xl font-bold leading-tight ${textCls}`}>{pet.name}</h3>
                  {pet.breed && (
                    <span className={`inline-block mt-1.5 px-2.5 py-0.5 text-xs font-medium rounded-full ${
                      lightTheme ? 'bg-amber-100 text-amber-700' : 'bg-amber-900/40 text-amber-300'
                    }`}>
                      {pet.breed}
                    </span>
                  )}
                </div>

                {(pet.species || pet.size || pet.weight != null || pet.sex || pet.color || pet.castrated != null) && (
                  <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                    {pet.species && (
                      <div>
                        <p className={`text-[10px] font-semibold uppercase tracking-wider mb-0.5 ${labelCls}`}>Espécie</p>
                        <p className={`text-sm font-medium ${textCls}`}>{translatePetField(pet.species, PET_SPECIES_LABELS)}</p>
                      </div>
                    )}
                    {pet.sex && (
                      <div>
                        <p className={`text-[10px] font-semibold uppercase tracking-wider mb-0.5 ${labelCls}`}>Sexo</p>
                        <p className={`text-sm font-medium ${textCls}`}>{translatePetField(pet.sex, SEX_LABELS)}</p>
                      </div>
                    )}
                    {pet.size && (
                      <div>
                        <p className={`text-[10px] font-semibold uppercase tracking-wider mb-0.5 ${labelCls}`}>Porte</p>
                        <p className={`text-sm font-medium ${textCls}`}>{translatePetField(pet.size, SIZE_LABELS)}</p>
                      </div>
                    )}
                    {pet.weight != null && (
                      <div>
                        <p className={`text-[10px] font-semibold uppercase tracking-wider mb-0.5 ${labelCls}`}>Peso</p>
                        <p className={`text-sm font-medium ${textCls}`}>{pet.weight} kg</p>
                      </div>
                    )}
                    {pet.color && (
                      <div>
                        <p className={`text-[10px] font-semibold uppercase tracking-wider mb-0.5 ${labelCls}`}>Cor</p>
                        <p className={`text-sm font-medium ${textCls}`}>{pet.color}</p>
                      </div>
                    )}
                    {pet.castrated != null && (
                      <div>
                        <p className={`text-[10px] font-semibold uppercase tracking-wider mb-0.5 ${labelCls}`}>Castrado</p>
                        <p className={`text-sm font-medium ${pet.castrated
                          ? (lightTheme ? 'text-green-600' : 'text-green-400')
                          : textCls}`}>
                          {pet.castrated ? 'Sim' : 'Não'}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="p-5 space-y-5">

              {/* Vacinas */}
              {pet.vaccines && (
                <div className={`border-t pt-4 ${divider}`}>
                  <h4 className={`text-[11px] font-semibold uppercase tracking-wider mb-1.5 ${labelCls}`}>Vacinas</h4>
                  <p className={`text-sm rounded-xl p-3 leading-relaxed ${descBg}`}>{pet.vaccines}</p>
                </div>
              )}

              {/* Description + Unit */}
              {(pet.description || pet.unit) && (
                <div className={`space-y-3 border-t pt-4 ${divider}`}>
                  {pet.description && (
                    <div>
                      <h4 className={`text-[11px] font-semibold uppercase tracking-wider mb-1.5 flex items-center gap-1 ${labelCls}`}>
                        <FileText className="h-3 w-3" />
                        Descrição
                      </h4>
                      <p className={`text-sm rounded-xl p-3 leading-relaxed ${descBg}`}>
                        {pet.description}
                      </p>
                    </div>
                  )}

                  {pet.unit && (
                    <div>
                      <h4 className={`text-[11px] font-semibold uppercase tracking-wider mb-1.5 flex items-center gap-1 ${labelCls}`}>
                        <Building2 className="h-3 w-3" />
                        {getUnitLabel(condoType)}
                      </h4>
                      <button
                        type="button"
                        onClick={handleUnitClick}
                        className={`group flex items-center gap-2 w-full text-left rounded-lg -mx-1 px-1 py-0.5 transition-colors ${
                          lightTheme ? 'hover:bg-gray-100' : 'hover:bg-gray-800'
                        }`}
                        title="Abrir unidade"
                      >
                        <div className="min-w-0 flex-1">
                          {pet.unit.street?.name ? (
                            <>
                              <p className={`text-sm font-medium truncate ${textCls}`}>
                                {pet.unit.street_number ? `${pet.unit.street.name}, ${pet.unit.street_number}` : pet.unit.street.name}
                              </p>
                              <p className={`text-xs truncate ${labelCls}`}>
                                {formatUnitLabel({ number: pet.unit.number, blockName: pet.unit.block?.name }, condoType)}
                              </p>
                            </>
                          ) : (
                            <span className={`text-sm font-medium truncate ${textCls}`}>
                              {formatUnitLabel({ number: pet.unit.number, blockName: pet.unit.block?.name }, condoType)}
                            </span>
                          )}
                        </div>
                        <ChevronRight className={`h-3.5 w-3.5 flex-shrink-0 ml-auto opacity-0 group-hover:opacity-100 transition-opacity ${labelCls}`} />
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Actions */}
              {confirmDelete ? (
                <div className={`rounded-xl p-4 space-y-3 border ${
                  lightTheme ? 'bg-red-50 border-red-200' : 'bg-red-950/20 border-red-900/40'
                }`}>
                  <p className={`text-sm text-center ${lightTheme ? 'text-red-600' : 'text-red-400'}`}>
                    Remover <span className="font-semibold">{pet.name}</span>?
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setConfirmDelete(false)}
                      className={`flex-1 px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${
                        lightTheme
                          ? 'text-gray-600 border-gray-200 hover:bg-gray-50'
                          : 'text-gray-400 border-gray-700 hover:bg-gray-800'
                      }`}
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={handleDelete}
                      disabled={deleting}
                      className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-white bg-red-500 hover:bg-red-600 rounded-lg transition-colors disabled:opacity-50"
                    >
                      {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                      {deleting ? 'Removendo...' : 'Confirmar'}
                    </button>
                  </div>
                </div>
              ) : (
                <div className={`flex items-center justify-between border-t pt-4 ${divider}`}>
                  {isEditMode ? (
                    <button
                      onClick={() => setIsEditMode(false)}
                      className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${
                        lightTheme
                          ? 'text-green-700 border-green-300 hover:bg-green-50'
                          : 'text-green-400 border-green-800 hover:bg-green-900/20'
                      }`}
                    >
                      <Check className="h-4 w-4" />
                      Concluir edição
                    </button>
                  ) : (
                    <button
                      onClick={() => { setIsEditMode(true); setShowEditForm(true); }}
                      className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${
                        lightTheme
                          ? 'text-gray-600 border-gray-200 hover:bg-gray-50'
                          : 'text-gray-400 border-gray-700 hover:bg-gray-800'
                      }`}
                    >
                      <Edit2 className="h-4 w-4" />
                      Editar
                    </button>
                  )}

                  <button
                    onClick={() => setConfirmDelete(true)}
                    className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                      lightTheme
                        ? 'text-red-400 hover:text-red-500 hover:bg-red-50'
                        : 'text-red-500/70 hover:text-red-400 hover:bg-red-950/20'
                    }`}
                  >
                    <Trash2 className="h-4 w-4" />
                    Excluir
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {lightboxPhoto && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/92"
          onClick={() => setLightboxPhoto(null)}
        >
          <button
            className="absolute top-4 right-4 text-white/60 hover:text-white transition-colors"
            onClick={() => setLightboxPhoto(null)}
          >
            <X className="h-6 w-6" />
          </button>
          <img
            src={lightboxPhoto}
            alt={pet.name}
            className="max-w-[92vw] max-h-[92vh] object-contain rounded-xl shadow-2xl"
            onClick={e => e.stopPropagation()}
          />
        </div>
      )}

      {/* Photo upload modal */}
      <PhotoUploadModal
        open={showPhotoModal}
        onClose={() => setShowPhotoModal(false)}
        onConfirm={handlePhotoCropped}
        title={`Foto ${activeSlot} do Pet`}
        cropShape="rect"
        aspectRatio={4 / 5}
        darkMode={!lightTheme}
        disableFaceDetection={true}
      />

      {showEditForm && (
        <PetFormModal
          lightTheme={lightTheme}
          customerId={customerId}
          pet={pet}
          unitId={pet.unit_id}
          onSuccess={handleEditSuccess}
          onClose={() => setShowEditForm(false)}
        />
      )}
    </>
  );
};

export default PetDetailModal;
